import React, { useEffect, useState } from "react";
import { getAllAdmins, createAdmin, updateAdminProfile, deleteAdmin, updateUserStatus } from "actions/action";
import { Tooltip } from "@mui/material";
import Swal from "sweetalert2";

export default function Admins() {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        admission_no: "",
        password: "",
    });
    const [creating, setCreating] = useState(false);
    const [validationError, setValidationError] = useState("");

    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({
        first_name: "",
        last_name: "",
        phone: "",
        admission_no: "",
    });

    useEffect(() => {
        fetchAdmins();
    }, []);

    const fetchAdmins = async () => {
        setLoading(true);
        try {
            const res = await getAllAdmins();
            if (res?.data && res.data.success) {
                setAdmins(res.data.data);
            } else {
                setError("Failed to load admins");
            }
        } catch {
            setError("Failed to load admins");
        } finally {
            setLoading(false);
        }
    };

    const validateCreate = () => {
        const { first_name, email, phone, admission_no, password } = createForm;
        if (!first_name || !email || !phone || !admission_no || !password) {
            setValidationError("All fields are required");
            return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setValidationError("Enter a valid email");
            return false;
        }
        if (password.length < 6) {
            setValidationError("Password must be at least 6 characters");
            return false;
        }
        setValidationError("");
        return true;
    };

    const handleCreate = async () => {
        if (!validateCreate()) return;
        setCreating(true);
        try {
            const res = await createAdmin(createForm);
            if (res?.success) {
                setShowCreate(false);
                setCreateForm({
                    first_name: "",
                    last_name: "",
                    email: "",
                    phone: "",
                    admission_no: "",
                    password: "",
                });
                fetchAdmins(); // Refresh list
            } else {
                setValidationError(res?.message || "Failed to create admin");
            }
        } catch {
            setValidationError("Failed to create admin");
        } finally {
            setCreating(false);
        }
    };

    const startEdit = (admin) => {
        setEditingId(admin.user_id);
        setEditForm({
            first_name: admin.first_name || "",
            last_name: admin.last_name || "",
            phone: admin.phone || "",
            admission_no: admin.admission_no || "",
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditForm({
            first_name: "",
            last_name: "",
            phone: "",
            admission_no: "",
        });
    };

    const saveEdit = async (id) => {
        try {
            const res = await updateAdminProfile(id, editForm);
            if (res?.success) {
                setAdmins(prev => prev.map(a => a.user_id === id ? { ...a, ...editForm } : a));
                cancelEdit();
                Swal.fire({
                    icon: 'success',
                    title: 'Updated!',
                    text: 'Admin profile updated successfully.',
                    timer: 1500,
                    showConfirmButton: false
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: res?.message || 'Failed to update admin',
                });
            }
        } catch {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update admin',
            });
        }
    };

    const handleStatusChange = async (id, currentStatus) => {
        const newStatus = !currentStatus;
        try {
            const res = await updateUserStatus(id, newStatus);
            // updateUserStatus returns the full axios response, so we check res.data
            if (res?.data?.success) {
                setAdmins(prev => prev.map(a => a.user_id === id ? { ...a, is_active: newStatus } : a));
                Swal.fire({
                    icon: 'success',
                    title: newStatus ? 'Activated!' : 'Deactivated!',
                    text: `Admin has been ${newStatus ? 'activated' : 'deactivated'}.`,
                    timer: 1500,
                    showConfirmButton: false
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: res?.data?.message || 'Failed to update status',
                });
            }
        } catch {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update status',
            });
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const res = await deleteAdmin(id);
                if (res?.success) {
                    setAdmins(prev => prev.filter(a => a.user_id !== id));
                    Swal.fire(
                        'Deleted!',
                        'Admin has been deleted.',
                        'success'
                    );
                } else {
                    Swal.fire(
                        'Error!',
                        res?.message || 'Failed to delete admin.',
                        'error'
                    );
                }
            } catch {
                Swal.fire(
                    'Error!',
                    'Failed to delete admin.',
                    'error'
                );
            }
        }
    };

    return (
        <>
            <div className="relative">
                <div className="flex justify-between items-center px-4 mt-6">
                    <p className="font-semibold text-lg text-white">Admins Management</p>
                </div>

                <div className="px-6 mt-4 mb-6">
                    <div className="flex flex-row flex-wrap items-center">
                        <Tooltip title="Create New Admin" arrow>
                            <button
                                onClick={() => setShowCreate((s) => !s)}
                                className="bg-lightBlue-500 text-white active:bg-lightBlue-600 font-semibold text-sm px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
                                type="button"
                            >
                                {showCreate ? "Hide Form" : "Create Admin"}
                            </button>
                        </Tooltip>
                    </div>
                </div>

                <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-blueGray-700">
                    {showCreate && (
                        <div className="px-4 py-4 bg-blueGray-800 border-b border-blueGray-600">
                            <h4 className="text-white font-semibold mb-3">Create New Admin</h4>
                            {validationError && (
                                <div className="mb-3 text-red-500 text-sm">
                                    {validationError}
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs mb-1 text-white">First Name</label>
                                    <input
                                        className="w-full border rounded px-3 py-2 bg-blueGray-700 text-white border-blueGray-600"
                                        value={createForm.first_name}
                                        onChange={(e) => setCreateForm(s => ({ ...s, first_name: e.target.value }))}
                                        placeholder="First Name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs mb-1 text-white">Last Name</label>
                                    <input
                                        className="w-full border rounded px-3 py-2 bg-blueGray-700 text-white border-blueGray-600"
                                        value={createForm.last_name}
                                        onChange={(e) => setCreateForm(s => ({ ...s, last_name: e.target.value }))}
                                        placeholder="Last Name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs mb-1 text-white">Email</label>
                                    <input
                                        className="w-full border rounded px-3 py-2 bg-blueGray-700 text-white border-blueGray-600"
                                        value={createForm.email}
                                        onChange={(e) => setCreateForm(s => ({ ...s, email: e.target.value }))}
                                        placeholder="Email"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs mb-1 text-white">Phone</label>
                                    <input
                                        className="w-full border rounded px-3 py-2 bg-blueGray-700 text-white border-blueGray-600"
                                        value={createForm.phone}
                                        onChange={(e) => setCreateForm(s => ({ ...s, phone: e.target.value }))}
                                        placeholder="Phone"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs mb-1 text-white">Employee/Admission No</label>
                                    <input
                                        className="w-full border rounded px-3 py-2 bg-blueGray-700 text-white border-blueGray-600"
                                        value={createForm.admission_no}
                                        onChange={(e) => setCreateForm(s => ({ ...s, admission_no: e.target.value }))}
                                        placeholder="Emp ID"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs mb-1 text-white">Password</label>
                                    <input
                                        type="password"
                                        className="w-full border rounded px-3 py-2 bg-blueGray-700 text-white border-blueGray-600"
                                        value={createForm.password}
                                        onChange={(e) => setCreateForm(s => ({ ...s, password: e.target.value }))}
                                        placeholder="Password"
                                    />
                                </div>
                            </div>
                            <div className="mt-4 flex justify-end gap-2">
                                <button
                                    onClick={() => setShowCreate(false)}
                                    className="bg-blueGray-500 text-white text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-md"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleCreate}
                                    disabled={creating}
                                    className="bg-emerald-500 text-white text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-md disabled:opacity-60"
                                >
                                    {creating ? "Creating..." : "Create Admin"}
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="block w-full overflow-x-auto p-4">
                        {loading && <div className="text-white">Loading...</div>}
                        {error && !loading && <div className="text-red-400">{error}</div>}
                        {!loading && !error && (
                            <table className="items-center w-full bg-transparent border-collapse">
                                <thead>
                                    <tr>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                                            First Name
                                        </th>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                                            Last Name
                                        </th>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                                            Email
                                        </th>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                                            Phone
                                        </th>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                                            Emp ID
                                        </th>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                                            Status
                                        </th>
                                        <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {admins.map((admin) => (
                                        <tr key={admin.user_id}>
                                            <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                                                {editingId === admin.user_id ? (
                                                    <input
                                                        className="w-full border rounded px-2 py-1 bg-blueGray-800 text-white border-blueGray-600"
                                                        value={editForm.first_name}
                                                        onChange={(e) => setEditForm(s => ({ ...s, first_name: e.target.value }))}
                                                    />
                                                ) : (
                                                    admin.first_name
                                                )}
                                            </td>
                                            <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                                                {editingId === admin.user_id ? (
                                                    <input
                                                        className="w-full border rounded px-2 py-1 bg-blueGray-800 text-white border-blueGray-600"
                                                        value={editForm.last_name}
                                                        onChange={(e) => setEditForm(s => ({ ...s, last_name: e.target.value }))}
                                                    />
                                                ) : (
                                                    admin.last_name
                                                )}
                                            </td>
                                            <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                                                {admin.email}
                                            </td>
                                            <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                                                {editingId === admin.user_id ? (
                                                    <input
                                                        className="w-full border rounded px-2 py-1 bg-blueGray-800 text-white border-blueGray-600"
                                                        value={editForm.phone}
                                                        onChange={(e) => setEditForm(s => ({ ...s, phone: e.target.value }))}
                                                    />
                                                ) : (
                                                    admin.phone
                                                )}
                                            </td>
                                            <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                                                {editingId === admin.user_id ? (
                                                    <input
                                                        className="w-full border rounded px-2 py-1 bg-blueGray-800 text-white border-blueGray-600"
                                                        value={editForm.admission_no}
                                                        onChange={(e) => setEditForm(s => ({ ...s, admission_no: e.target.value }))}
                                                    />
                                                ) : (
                                                    admin.admission_no
                                                )}
                                            </td>
                                            <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                                                <button
                                                    onClick={() => handleStatusChange(admin.user_id, admin.is_active)}
                                                    className={`${admin.is_active ? "bg-emerald-500" : "bg-red-500"
                                                        } text-white font-bold uppercase text-xs px-2 py-1 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150`}
                                                >
                                                    {admin.is_active ? "Active" : "Inactive"}
                                                </button>
                                            </td>
                                            <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                                                {editingId === admin.user_id ? (
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => saveEdit(admin.user_id)}
                                                            className="text-emerald-500 hover:text-emerald-400 font-bold uppercase text-xs outline-none focus:outline-none ease-linear transition-all duration-150"
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            onClick={cancelEdit}
                                                            className="text-red-500 hover:text-red-400 font-bold uppercase text-xs outline-none focus:outline-none ease-linear transition-all duration-150"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex gap-4">
                                                        <button
                                                            onClick={() => startEdit(admin)}
                                                            className="text-lightBlue-500 hover:text-lightBlue-400 font-bold uppercase text-xs outline-none focus:outline-none ease-linear transition-all duration-150"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(admin.user_id)}
                                                            className="text-red-500 hover:text-red-400 font-bold uppercase text-xs outline-none focus:outline-none ease-linear transition-all duration-150"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                )}

                                            </td>
                                        </tr>
                                    ))}
                                    {admins.length === 0 && (
                                        <tr>
                                            <td colSpan="7" className="text-center p-4 text-white">No admins found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
